/**
 * DoneGrading UI string bundles — map to Tailwind `dg.*` tokens + radii.
 * Prefer these over one-off strings for surfaces, borders, and dense UI type.
 */

// --- Layout surfaces ---

export const cockpit =
  'shrink-0 rounded-dg-card border border-dg-border bg-dg-bg-surface/95 dark:border-slate-700 dark:bg-slate-900/90 p-2 shadow-dg-card dark:shadow-dg-soft-dark backdrop-blur-sm';

/** Primary in-viewport panel — no main-column scroll; paginate or segment inside. */
export const surfacePanel =
  'flex-1 min-h-0 min-w-0 overflow-hidden overflow-x-hidden overscroll-x-none rounded-dg-card border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900 p-4 space-y-3 shadow-dg-card dark:shadow-dg-soft-dark';

export const surfaceScroll =
  'flex-1 min-h-0 min-w-0 overflow-hidden overflow-x-hidden overscroll-x-none rounded-dg-card border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900 p-3 space-y-2 shadow-dg-card dark:shadow-dg-soft-dark';

export const surfaceScrollPlain = 'flex-1 min-h-0 min-w-0 overflow-hidden overflow-x-hidden';

/** Modals, dropdowns, and nested pickers only. */
export const surfaceScrollNested =
  'max-h-56 overflow-y-auto overflow-x-hidden custom-scrollbar overscroll-contain';

export const surfaceModal =
  'rounded-dg-modal border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-950 shadow-dg-modal overflow-hidden';

export const surfaceCollapsible =
  'rounded-dg-control border border-dg-border bg-dg-bg-surface/95 dark:border-slate-700 dark:bg-slate-950/80 overflow-hidden shadow-dg-card';

export const dividerHorizontal = 'border-b border-dg-border dark:border-slate-700';

// --- Cards ---

export const card =
  'rounded-dg-card border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900 p-4 shadow-dg-card dark:shadow-dg-soft-dark';

export const cardTight =
  'rounded-dg-card border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900 p-3 shadow-dg-card dark:shadow-dg-soft-dark';

export const cardLarge =
  'rounded-dg-card border border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900 p-5 shadow-dg-card dark:shadow-dg-soft-dark';

// --- Typography (dense product UI — pair with .dg-type-* for marketing blocks) ---

export const sectionTitle =
  'text-dg-label font-semibold text-dg-fg-muted dark:text-slate-400 tracking-wide uppercase';

export const label = 'text-dg-label font-medium text-dg-fg-secondary dark:text-slate-300';

export const textBody = 'text-dg-body text-dg-fg-primary dark:text-slate-100';

export const textMeta = 'text-dg-secondary text-dg-fg-muted dark:text-slate-400';

export const helperText = 'text-[11px] text-dg-fg-muted dark:text-slate-500';

// --- Form controls ---

export const input =
  'min-h-[44px] px-3 py-2 rounded-dg-control border border-dg-border bg-dg-bg-surface dark:border-slate-600 dark:bg-slate-900 text-dg-body text-dg-fg-primary outline-none transition-colors focus:border-dg-primary-500 focus:ring-1 focus:ring-dg-primary-500/30';

export const textarea =
  'w-full px-3 py-2 rounded-dg-control border border-dg-border bg-dg-bg-surface dark:border-slate-600 dark:bg-slate-900 text-dg-body text-dg-fg-primary resize-none custom-scrollbar outline-none transition-colors focus:border-dg-primary-500 focus:ring-1 focus:ring-dg-primary-500/30';

// --- Buttons ---

export const btnPrimary =
  'w-full min-h-11 py-2.5 rounded-dg-control text-dg-button font-medium bg-dg-primary-600 text-white shadow-dg-card hover:bg-dg-primary-700 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-45 disabled:pointer-events-none';

export const btnSecondary =
  'w-full min-h-11 py-2.5 rounded-dg-control text-dg-button font-medium border border-dg-border bg-dg-bg-surface dark:border-slate-600 dark:bg-slate-900 text-dg-fg-primary dark:text-slate-100 hover:bg-dg-interaction-hover dark:hover:bg-slate-800 active:scale-[0.99] transition-all flex items-center justify-center gap-2';

export const btnGhost =
  'py-2 text-dg-secondary font-medium text-dg-fg-muted hover:text-dg-primary-600 dark:hover:text-dg-primary-400 underline underline-offset-2';

export const btnPill =
  'px-3 py-1 rounded-full text-dg-label font-medium border border-dg-border bg-dg-bg-muted dark:border-slate-600 dark:bg-slate-800';

// --- Chips ---

export const chip = 'px-2.5 py-1 rounded-full text-dg-label font-medium border transition-colors';
export const chipActive = 'bg-dg-primary-500 text-white border-dg-primary-500';
export const chipInactive =
  'bg-dg-bg-surface dark:bg-slate-900 text-dg-fg-secondary dark:text-slate-300 border-dg-border dark:border-slate-600';

// --- Lists ---

export const listItem =
  'w-full text-left px-3 py-2.5 rounded-dg-control text-dg-body border transition-colors';
export const listItemActive =
  'bg-dg-primary-50 dark:bg-dg-primary-950/50 border-indigo-200 dark:border-dg-primary-700 text-dg-primary-700 dark:text-indigo-100';
export const listItemInactive =
  'bg-dg-bg-surface dark:bg-slate-900 border-dg-border dark:border-slate-700 text-dg-fg-primary dark:text-slate-100 hover:bg-dg-interaction-hover dark:hover:bg-slate-800';

export const gap = 'gap-2';
export const spaceY = 'space-y-2';
export const spaceY3 = 'space-y-3';

export const borderDefault = 'border-dg-border dark:border-slate-700';
